import { useEffect, useMemo, useState } from "react";
import BaseSettingsPage from "@settings/base/BaseSettingsPage";
import SettingsCard from "@settings/base/SettingsCard";
import {
    Mutation,
    MutationCreateInviteLinkArgs,
    MutationCreateUserInCompanyArgs,
    MutationDeleteInviteLinkArgs,
    MutationDeleteUserArgs,
    MutationUpdateQuotaDistributionArgs,
    MutationUpdateUserArgs,
    Query,
    User,
    UserRole,
    UserSize,
} from "@/app/graphql/codegen";
import { FETCH_COMPANY_SEATS } from "@company/graphql/queries/fetchCompanySeats";
import { UPDATE_QUOTA_DISTRIBUTION } from "@company/graphql/mutations/updateQuotaDistribution";
import { useMutation, useQuery } from "@apollo/client";
import {
    Alert,
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { UPDATE_USER_ROLE } from "@company/graphql/mutations/updateUserRole";
import { CREATE_USER_IN_COMPANY } from "@company/graphql/mutations/createUserInCompany";
import { DELETE_USER } from "@company/graphql/mutations/deleteUser";
import { validateEmail } from "@/utils/validateCredentials";
import { CREATE_INVITE_LINK } from "@company/graphql/mutations/createInviteLink";
import { DELETE_INVITE_LINK } from "@company/graphql/mutations/deleteInviteLink";
import { DEBUG_UPDATE_COMPANY_SEATS } from "@company/graphql/mutations/debugUpdateCompanySeats";
import { SeatManagementRow } from "../../components/SeatManagementRow";
import AddIcon from "@mui/icons-material/Add";
import { triggerNotification } from "@store/slices/notification";
import { useDispatch } from "react-redux";
import { calculateSeatOccupancy } from "@/utils/seatUtils";
import { formatNumber } from "@/utils/format";
import { useNavigate } from "react-router-dom";
import { RouteToSettings, RouteTo } from "@routing/RouteMap";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";
import { setIsSwitchUserDialogOpen, setSelectedSwitchSeatType, useUserSlice } from "@store/slices/user";
import { SELECT_OWNER_SEAT } from "@company/graphql/mutations/selectOwnerSeat";
import { SeatSwapDialog } from "../../components/SeatSwapDialog";
import { SWAP_USER_SEATS } from "../../graphql/mutations/swapUserSeats";

// Typendefinitionen zur Behebung von Typenproblemen
// Diese Schnittstellen repräsentieren die Form der Objekte, wie sie von der API kommen
interface ExtendedUser extends User {
    id: string;
    name: string;
    pageQuota: number;
    usedPages: number;
    role: UserRole;
    size: UserSize;
}

interface ExtendedInviteLink {
    id: string;
    code: string;
    email: string;
    role: UserRole;
    size: UserSize;
    isUsed: boolean;
    createdAt: string;
}

interface ExtendedCompany {
    id: string;
    name: string;
    freePageQuota: number;
    ownerId: string;
    subscription: {
        id: string;
        seatsS: number;
        seatsM: number;
        seatsL: number;
    };
    users: ExtendedUser[];
    inviteLinks: ExtendedInviteLink[];
}

export const UserManagementPage = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isSwitchUserDialogOpen, selectedSwitchSeatType } = useUserSlice();
    const [selectOwnerSeatMutation, { data: selectedOwnerSeatData }] = useMutation(SELECT_OWNER_SEAT);

    // ==================== Abfragen ====================
    const { data, loading, refetch } = useQuery<Pick<Query, "company">>(FETCH_COMPANY_SEATS);

    const company = data?.company as unknown as ExtendedCompany;
    const users = company?.users || [];
    const inviteLinks = company?.inviteLinks || [];
    const unusedInviteLinks = inviteLinks.filter((link: ExtendedInviteLink) => !link.isUsed);
    const subscription = company?.subscription;

    // Sitzplatzanzahl aus dem Abonnement
    const seatsS = subscription?.seatsS || 0;
    const seatsM = subscription?.seatsM || 0;
    const seatsL = subscription?.seatsL || 0;

    // ==================== State für Sitzplatzverwaltung ====================
    const [debugSeats, setDebugSeats] = useState({
        S: seatsS,
        M: seatsM,
        L: seatsL,
    });

    // ==================== State für Fehlerbehandlung ====================
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "success" | "info" | "warning">("error");

    // ==================== State für Quota-Verwaltung ====================
    const [quotaState, setQuotaState] = useState<{
        userQuotas: { userId: string; pageQuota: number }[];
        companyQuota: number;
    }>({
        userQuotas: [],
        companyQuota: 0,
    });

    // ==================== State für Rollen-Verwaltung ====================
    const [roleState, setRoleState] = useState<{
        userRoles: { userId: string; role: UserRole }[];
    }>({
        userRoles: [],
    });

    // ==================== State für Size-Verwaltung ====================
    const [sizeState, setSizeState] = useState<{
        userSizes: { userId: string; size: UserSize }[];
    }>({
        userSizes: [],
    });

    // ==================== Benutzer-Verwaltung Dialoge und States ====================
    // Nutzer löschen
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    // ==================== Einladungslink-Verwaltung Dialoge und States ====================
    const [openInviteDialog, setOpenInviteDialog] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.Editor);
    const [inviteSize, setInviteSize] = useState<UserSize>(UserSize.S);
    const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});

    // Einladungslink löschen
    const [inviteDeleteDialogOpen, setInviteDeleteDialogOpen] = useState(false);
    const [inviteLinkToDelete, setInviteLinkToDelete] = useState<any | null>(null);

    // ==================== User Swap States ====================
    const [swapDialogOpen, setSwapDialogOpen] = useState(false);
    const [sourceUserForSwap, setSourceUserForSwap] = useState<User | null>(null);
    const [sourceInviteLinkForSwap, setSourceInviteLinkForSwap] = useState<any | null>(null);
    const [swapError, setSwapError] = useState<string>("");

    // ==================== State für Sitzplatzverwaltungsdialog ====================
    const [addSeatsDialogOpen, setAddSeatsDialogOpen] = useState(false);
    const [seatQuantities, setSeatQuantities] = useState({
        S: 0,
        M: 0,
        L: 0,
    });

    // ==================== State für Quota-Verteilung Dialog ====================
    const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
    const [distribution, setDistribution] = useState<Record<string, string>>({});

    // ==================== Mutationen ====================
    const [createUserInCompany] = useMutation<Pick<Mutation, "createUserInCompany">, MutationCreateUserInCompanyArgs>(CREATE_USER_IN_COMPANY, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onError: (error) => {
            setSnackbarMessage(`Fehler: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        },
    });

    const [deleteUser] = useMutation<Pick<Mutation, "deleteUser">, MutationDeleteUserArgs>(DELETE_USER, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onError: (error) => {
            setSnackbarMessage(`Fehler: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        },
    });

    const [createInviteLink] = useMutation<Pick<Mutation, "createInviteLink">, MutationCreateInviteLinkArgs>(CREATE_INVITE_LINK, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onError: (error) => {
            setSnackbarMessage(`Fehler: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        },
    });

    const [deleteInviteLink] = useMutation<Pick<Mutation, "deleteInviteLink">, MutationDeleteInviteLinkArgs>(DELETE_INVITE_LINK, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onError: (error) => {
            setSnackbarMessage(`Fehler: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        },
    });

    // Debug-Mutation zur Aktualisierung der Sitzplätze
    const [debugUpdateCompanySeats] = useMutation(DEBUG_UPDATE_COMPANY_SEATS, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onError: (error) => {
            setSnackbarMessage(`Fehler: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        },
        onCompleted: () => {
            setSnackbarMessage("Sitzplätze erfolgreich aktualisiert");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        },
    });

    // Update-Mutationen
    const [updateQuotaDistribution, { loading: updating }] = useMutation<Pick<Mutation, "updateQuotaDistribution">, MutationUpdateQuotaDistributionArgs>(
        UPDATE_QUOTA_DISTRIBUTION,
        {
            refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        },
    );

    const [updateUserProfile] = useMutation<Pick<Mutation, "updateUser">, MutationUpdateUserArgs>(UPDATE_USER_ROLE, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onError: (error) => {
            setSnackbarMessage(`Fehler: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        },
    });

    // Swap User Seats Mutation
    const [swapUserSeats, { loading: swapping }] = useMutation(SWAP_USER_SEATS, {
        refetchQueries: [{ query: FETCH_COMPANY_SEATS }],
        onCompleted: (data) => {
            if (data.swapUserSeats.success) {
                setSnackbarMessage("Plätze erfolgreich getauscht!");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                // setConfirmSwapDialogOpen(false); // This state is now managed by SeatSwapDialog
                setSwapDialogOpen(false);
                setSourceUserForSwap(null);
                // setTargetUserForSwap(null); // This state is now managed by SeatSwapDialog
                setSwapError("");
            } else {
                setSwapError(data.swapUserSeats.errors?.join(", ") || "Unbekannter Fehler beim Tausch");
            }
        },
        onError: (error) => {
            setSwapError(`Fehler beim Tauschen: ${error.message}`);
        },
    });

    useEffect(() => {
        refetch();
    }, [selectedOwnerSeatData]);

    // ==================== Berechnungen und Validierungen ====================
    const [originalTotal, setOriginalTotal] = useState(0);
    const [hasChanges, setHasChanges] = useState(false);

    // Berechnung der ursprünglichen Gesamtanzahl und der aktuellen Nutzer-Zuweisungssumme
    const userAssignedTotal = useMemo(() => {
        return quotaState.userQuotas.reduce((sum, u) => sum + u.pageQuota, 0);
    }, [quotaState.userQuotas]);

    const currentCompanyQuota = useMemo(() => {
        // Wenn die Nutzerzuweisung kleiner als die Originalsumme ist, geht der Rest an die Firma
        return originalTotal - userAssignedTotal;
    }, [originalTotal, userAssignedTotal]);

    // Prüfen ob die aktuelle Zuweisungssumme die Originalsumme überschreitet
    const isValidTotal = userAssignedTotal <= originalTotal;

    // Prüfen ob alle Nutzerkontingente gültig sind (nicht weniger als verbrauchte Seiten)
    const areUserQuotasValid = useMemo(() => {
        if (!users.length) return true;
        return quotaState.userQuotas.every((quotaItem) => {
            const user = users.find((u: ExtendedUser) => u.id === quotaItem.userId);
            return user ? quotaItem.pageQuota >= (user.usedPages || 0) : true;
        });
    }, [quotaState.userQuotas, users]);

    // Überprüfen, welche Benutzer ungültige Kontingent-Werte haben
    const invalidUserQuotas = useMemo(() => {
        if (!users.length) return {};
        const invalidUsers: Record<string, boolean> = {};

        quotaState.userQuotas.forEach((quotaItem) => {
            const user = users.find((u: ExtendedUser) => u.id === quotaItem.userId);
            if (user && quotaItem.pageQuota < (user.usedPages || 0)) {
                invalidUsers[quotaItem.userId] = true;
            }
        });

        return invalidUsers;
    }, [quotaState.userQuotas, users]);

    // ==================== Sitzplätze verarbeiten ====================
    // Arrays von Sitzplätzen für jede Größenkategorie erstellen
    const seatArrays = useMemo(() => {
        if (!company) return { S: [], M: [], L: [] };

        // Benutzer und Einladungen nach Größe organisieren
        const usersBySize = {
            S: users.filter((u: ExtendedUser) => u.size === UserSize.S),
            M: users.filter((u: ExtendedUser) => u.size === UserSize.M),
            L: users.filter((u: ExtendedUser) => u.size === UserSize.L),
        };

        const invitesBySize = {
            S: unusedInviteLinks.filter((i: ExtendedInviteLink) => i.size === UserSize.S),
            M: unusedInviteLinks.filter((i: ExtendedInviteLink) => i.size === UserSize.M),
            L: unusedInviteLinks.filter((i: ExtendedInviteLink) => i.size === UserSize.L),
        };

        // Arrays mit Sitzplatzzuweisungen erstellen
        return {
            S: Array.from({ length: seatsS }).map((_, i) => ({
                user: usersBySize.S[i],
                inviteLink: !usersBySize.S[i] ? invitesBySize.S[i - usersBySize.S.length] : undefined,
            })),
            M: Array.from({ length: seatsM }).map((_, i) => ({
                user: usersBySize.M[i],
                inviteLink: !usersBySize.M[i] ? invitesBySize.M[i - usersBySize.M.length] : undefined,
            })),
            L: Array.from({ length: seatsL }).map((_, i) => ({
                user: usersBySize.L[i],
                inviteLink: !usersBySize.L[i] ? invitesBySize.L[i - usersBySize.L.length] : undefined,
            })),
        };
    }, [company, users, unusedInviteLinks, seatsS, seatsM, seatsL]);

    // Sitzplatzbelegung mit der Hilfsfunktion berechnen
    const seatOccupancy = useMemo(() => {
        if (!company || !subscription) {
            return {
                S: { occupied: 0, total: 0 },
                M: { occupied: 0, total: 0 },
                L: { occupied: 0, total: 0 },
                total: { occupied: 0, total: 0 },
            };
        }

        return calculateSeatOccupancy(
            users as any[], // Cast ist aufgrund von Typendefinitionsunterschieden erforderlich
            subscription,
            inviteLinks as any[], // Cast ist aufgrund von Typendefinitionsunterschieden erforderlich
        );
    }, [company, users, subscription, inviteLinks]);

    // Prüfen, ob für jede Größe leere Sitzplätze verfügbar sind
    const emptySeatsAvailable = useMemo(() => {
        return {
            S: seatArrays.S.some((seat) => !seat.user && !seat.inviteLink),
            M: seatArrays.M.some((seat) => !seat.user && !seat.inviteLink),
            L: seatArrays.L.some((seat) => !seat.user && !seat.inviteLink),
        };
    }, [seatArrays]);

    // Array der Sitzplatzgrößen, die aktuell frei sind (für SeatSwapDialog)
    const emptySeatSizes: UserSize[] = useMemo(() => {
        return (Object.entries(emptySeatsAvailable) as [UserSize, boolean][])
            .filter(([, available]) => available)
            .map(([size]) => size as UserSize);
    }, [emptySeatsAvailable]);

    // Funktion zum Ermitteln der verfügbaren Sitzplatzgrößen, die nicht dem aktuellen Sitzplatztyp entsprechen
    const getAvailableSeats = () => {
        if (!selectedSwitchSeatType) return [];

        // Filtere die verfügbaren Sitzplatzgrößen, die nicht dem aktuellen Sitzplatztyp entsprechen
        return (Object.entries(emptySeatsAvailable) as [UserSize, boolean][]).filter(([size, available]) => available && size !== selectedSwitchSeatType).map(([size]) => size);
    };

    // Funktion zum Behandeln des Sitzplatzwechsels
    const handleSeatSwitch = async (newSize: UserSize) => {
        if (!selectedSwitchSeatType) return;

        try {
            // Hier würde normalerweise ein API-Aufruf erfolgen, um den Sitzplatzwechsel durchzuführen
            // Da dies nicht implementiert ist, zeigen wir nur eine Erfolgsmeldung an

            selectOwnerSeatMutation({ variables: { seatType: newSize } });

            // Dialog schließen
            dispatch(setIsSwitchUserDialogOpen(false));
            dispatch(setSelectedSwitchSeatType(null));

            // Erfolgsmeldung anzeigen
            dispatch(
                triggerNotification({
                    type: "success",
                    title: "Sitzplatz gewechselt",
                    content: `Der Sitzplatz wurde erfolgreich von ${selectedSwitchSeatType} zu ${newSize} gewechselt.`,
                    autoHideDuration: 3000,
                }),
            );
        } catch (error) {
            console.error("Error switching seat:", error);

            // Fehlermeldung anzeigen
            dispatch(
                triggerNotification({
                    type: "error",
                    title: "Fehler",
                    content: `Der Sitzplatz konnte nicht gewechselt werden.`,
                    autoHideDuration: 5000,
                }),
            );
        }
    };

    // ==================== Berechnungen für Quota-Verteilung ====================
    // Berechnung der Summe der zu verteilenden Seiten
    const totalDistribution: number = useMemo(() => Object.values(distribution).reduce((sum, value) => sum + (value === "" ? 0 : parseInt(value) || 0), 0), [distribution]);

    // Berechnung der verbleibenden Seiten im Firmenkontingent
    const remainingCompanyQuota: number = useMemo(() => (company?.freePageQuota || 0) - totalDistribution, [company?.freePageQuota, totalDistribution]);

    // ==================== Effekt-Hooks ====================
    // State initialisieren, wenn Daten geladen werden
    useEffect(() => {
        if (company && users.length > 0) {
            const initialUserQuotas = users.map((user: ExtendedUser) => ({
                userId: user.id,
                pageQuota: user.pageQuota || 0,
            }));

            setQuotaState({
                userQuotas: initialUserQuotas,
                companyQuota: company.freePageQuota || 0,
            });

            const total = (company.freePageQuota || 0) + users.reduce((sum: number, user: ExtendedUser) => sum + (user.pageQuota || 0), 0);

            setOriginalTotal(total);

            setRoleState({
                userRoles: users.map((user: ExtendedUser) => ({
                    userId: user.id,
                    role: user.role || UserRole.Editor,
                })),
            });

            setSizeState({
                userSizes: users.map((user: ExtendedUser) => ({
                    userId: user.id,
                    size: user.size || UserSize.S,
                })),
            });

            setHasChanges(false);
        }
    }, [company, users]);

    // ==================== Handler für Benutzer-Verwaltung ====================
    // Nutzer-Quota ändern
    const handleUserQuotaChange = (userId: string, value: number) => {
        // Quota-Änderung deaktiviert - Funktion für Schnittstellenkompatibilität beibehalten
        // setQuotaState((prev) => ({
        //     ...prev,
        //     userQuotas: prev.userQuotas.map((u) => (u.userId === userId ? { ...u, pageQuota: value } : u)),
        // }));
        // setHasChanges(true);
    };

    // Rolle ändern
    const handleRoleChange = async (userId: string, newRole: UserRole) => {
        console.log("handleRoleChange", userId, newRole);

        // Find the user
        const user = users.find((u: ExtendedUser) => u.id === userId);

        // Skip if user not found or is the owner
        if (!user || user.id === company?.ownerId) {
            console.log("Cannot change role: User not found or is the company owner");
            return;
        }

        try {
            // Immediately update the role in the backend
            await updateUserProfile({
                variables: {
                    id: userId,
                    role: newRole,
                },
            });

            // Update local state
            setRoleState((prev) => ({
                ...prev,
                userRoles: prev.userRoles.map((u) => (u.userId === userId ? { ...u, role: newRole } : u)),
            }));

            // Show success notification
            dispatch(
                triggerNotification({
                    type: "success",
                    title: "Rolle geändert",
                    content: `Die Rolle wurde erfolgreich geändert.`,
                    autoHideDuration: 3000,
                }),
            );
        } catch (error) {
            console.error("Error updating user role:", error);

            // Show error notification
            dispatch(
                triggerNotification({
                    type: "error",
                    title: "Fehler",
                    content: `Die Rolle konnte nicht geändert werden.`,
                    autoHideDuration: 5000,
                }),
            );
        }
    };

    // Größe ändern
    const handleSizeChange = async (userId: string, newSize: "S" | "M" | "L") => {
        try {
            // Immediately update the size in the backend
            await updateUserProfile({
                variables: {
                    id: userId,
                    size: newSize,
                },
            });

            // Update local state
            setSizeState((prev) => ({
                ...prev,
                userSizes: prev.userSizes.map((u) => (u.userId === userId ? { ...u, size: newSize as UserSize } : u)),
            }));

            // Show success notification
            dispatch(
                triggerNotification({
                    type: "success",
                    title: "Größe geändert",
                    content: `Die Größe wurde erfolgreich geändert.`,
                    autoHideDuration: 3000,
                }),
            );
        } catch (error) {
            console.error("Error updating user size:", error);

            // Show error notification
            dispatch(
                triggerNotification({
                    type: "error",
                    title: "Fehler",
                    content: `Die Größe konnte nicht geändert werden.`,
                    autoHideDuration: 5000,
                }),
            );
        }
    };

    // Benutzer löschen Dialoge
    const handleDeleteClick = (user: User) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        try {
            await deleteUser({
                variables: { id: userToDelete.id },
            });

            // Lokalen State aktualisieren
            setQuotaState((prev) => ({
                ...prev,
                userQuotas: prev.userQuotas.filter((u) => u.userId !== userToDelete.id),
            }));

            setRoleState((prev) => ({
                ...prev,
                userRoles: prev.userRoles.filter((u) => u.userId !== userToDelete.id),
            }));

            setSizeState((prev) => ({
                ...prev,
                userSizes: prev.userSizes.filter((u) => u.userId !== userToDelete.id),
            }));

            handleCloseDeleteDialog();

            // Benachrichtigung anzeigen, wenn Benutzer gelöscht wird
            dispatch(
                triggerNotification({
                    type: "info",
                    title: "Benutzer gelöscht",
                    content: `Benutzer "${userToDelete.name}" wurde erfolgreich gelöscht.`,
                    autoHideDuration: 5000,
                }),
            );
        } catch (error) {
            console.error("Error deleting user:", error);
            handleCloseDeleteDialog();
        }
    };

    // ==================== Handler für Einladungslink-Verwaltung ====================
    // Einladungslink-Validierung
    const validateInvite = () => {
        const errors: Record<string, string> = {};

        const emailError = validateEmail(inviteEmail);
        if (emailError) errors.email = emailError;

        setInviteErrors(errors);
        return errors;
    };

    // Einladungslink erstellen
    const handleCreateInviteLink = async () => {
        try {
            const errors = validateInvite();
            if (Object.keys(errors).length > 0) {
                return;
            }

            await createInviteLink({
                variables: { email: inviteEmail, role: inviteRole, size: inviteSize },
            });
            setOpenInviteDialog(false);
            setInviteEmail("");
            setInviteRole(UserRole.Editor);
            setInviteSize(UserSize.S);
            setInviteErrors({});

            // Benachrichtigung bei erfolgreicher Erstellung des Einladungslinks anzeigen
            dispatch(
                triggerNotification({
                    type: "success",
                    title: "Einladungslink erstellt",
                    content: `Einladungslink für ${inviteEmail} wurde erfolgreich erstellt.`,
                    autoHideDuration: 5000,
                }),
            );
        } catch (error) {
            console.error("Error creating invite link:", error);
            setInviteErrors({ submit: "Fehler beim Erstellen des Einladungslinks." });
        }
    };

    // Dialog zum Erstellen eines Einladungslinks schließen
    const handleCloseInviteDialog = () => {
        setOpenInviteDialog(false);
        setInviteErrors({});
    };

    // Einladungslink löschen Dialoge
    const handleInviteDeleteClick = (inviteLink: any) => {
        setInviteLinkToDelete(inviteLink);
        setInviteDeleteDialogOpen(true);
    };

    const handleCloseInviteDeleteDialog = () => {
        setInviteDeleteDialogOpen(false);
        setInviteLinkToDelete(null);
    };

    const handleConfirmInviteDelete = async () => {
        if (!inviteLinkToDelete) return;

        try {
            await deleteInviteLink({
                variables: { id: inviteLinkToDelete.id },
            });

            handleCloseInviteDeleteDialog();

            // Benachrichtigung anzeigen, wenn Einladungslink gelöscht wird
            dispatch(
                triggerNotification({
                    type: "info",
                    title: "Einladungslink gelöscht",
                    content: `Einladungslink für ${inviteLinkToDelete.email} wurde gelöscht.`,
                    autoHideDuration: 5000,
                }),
            );
        } catch (error) {
            console.error("Error deleting invite link:", error);
            handleCloseInviteDeleteDialog();
        }
    };

    // ==================== User Swap Handlers ====================
    const handleSwapClick = (user: User) => {
        setSourceUserForSwap(user);
        setSwapDialogOpen(true);
        setSwapError("");
    };

    const handleSwapDialogClose = () => {
        setSwapDialogOpen(false);
        setSourceUserForSwap(null);
        setSourceInviteLinkForSwap(null);
        setSwapError("");
    };

    // ==================== Invite Link Swap Handlers ====================
    const handleInviteLinkSwapClick = (inviteLink: any) => {
        setSourceInviteLinkForSwap(inviteLink);
        setSwapDialogOpen(true);
        setSwapError("");
    };

    // Legacy two-step swap handlers removed – SeatSwapDialog handles everything

    // ==================== Event-Handler für Sitzplatzverwaltung ====================
    // Dialog zum Hinzufügen von Sitzplätzen öffnen
    const handleOpenAddSeatsDialog = () => {
        setSeatQuantities({
            S: 0,
            M: 0,
            L: 0,
        });
        setAddSeatsDialogOpen(true);
    };

    // Dialog zum Hinzufügen von Sitzplätzen schließen
    const handleCloseAddSeatsDialog = () => {
        setAddSeatsDialogOpen(false);
    };

    // Änderung der Sitzplatzanzahl behandeln
    const handleSeatQuantityChange = (size: UserSize, value: number) => {
        setSeatQuantities((prev) => ({
            ...prev,
            [size]: Math.max(0, value),
        }));
    };

    // Bestätigung zum Hinzufügen von Sitzplätzen
    const handleConfirmAddSeats = () => {
        if (!company) return;

        // Nur fortfahren, wenn mindestens ein Sitzplatztyp hinzugefügt wird
        if (seatQuantities.S === 0 && seatQuantities.M === 0 && seatQuantities.L === 0) {
            return;
        }

        const newCount = {
            S: debugSeats.S + seatQuantities.S,
            M: debugSeats.M + seatQuantities.M,
            L: debugSeats.L + seatQuantities.L,
        };

        setDebugSeats(newCount);

        debugUpdateCompanySeats({
            variables: {
                companyId: company.id,
                seatsS: newCount.S,
                seatsM: newCount.M,
                seatsL: newCount.L,
            },
        });

        handleCloseAddSeatsDialog();
    };

    // Einladung eines Benutzers für eine bestimmte Sitzplatzgröße
    const handleCreateInviteForSize = (size: UserSize) => {
        setInviteSize(size);
        setOpenInviteDialog(true);
    };

    // Snackbar schließen
    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    // Entfernen eines Sitzplatzes (Debug)
    const handleRemoveSeat = (size: UserSize) => {
        if (!company) return;

        const newCount = {
            S: size === "S" ? Math.max(0, debugSeats.S - 1) : debugSeats.S,
            M: size === "M" ? Math.max(0, debugSeats.M - 1) : debugSeats.M,
            L: size === "L" ? Math.max(0, debugSeats.L - 1) : debugSeats.L,
        };

        setDebugSeats(newCount);

        debugUpdateCompanySeats({
            variables: {
                companyId: company.id,
                seatsS: newCount.S,
                seatsM: newCount.M,
                seatsL: newCount.L,
            },
        });
    };

    // ==================== Handler für Quota-Verteilung ====================
    // Initialisierung der Verteilung bei Dialog-Öffnung
    const handleOpenDistributeDialog = () => {
        // Setze für jeden Nutzer 0 als Anfangswert der Verteilung
        const initialDistribution: Record<string, string> = {};
        users.forEach((user: ExtendedUser) => {
            initialDistribution[user.id] = "0";
        });
        setDistribution(initialDistribution);
        setDistributeDialogOpen(true);
    };

    // Schließen des Verteilungs-Dialogs ohne Änderungen
    const handleCloseDistributeDialog = () => {
        setDistributeDialogOpen(false);
    };

    // Anpassung der Verteilung für einen Nutzer
    const handleAdjustDistribution = (userId: string, delta: number) => {
        setDistribution((prev) => {
            const currentValue = prev[userId] === undefined ? 0 : prev[userId] === "" ? 0 : parseInt(prev[userId]) || 0;
            return {
                ...prev,
                [userId]: String(Math.max(0, currentValue + delta)),
            };
        });
    };

    // Direkte Eingabe für die Verteilung für einen Nutzer
    const handleSetDistribution = (userId: string, value: string) => {
        // Erlaube leere Strings im Textfeld
        setDistribution((prev) => ({
            ...prev,
            [userId]: value,
        }));
    };

    // Verteilung bestätigen und speichern
    const handleConfirmDistribution = async () => {
        if (!company) return;

        try {
            // Erstelle die userQuotas array mit aktualisierten Werten
            const userQuotas = users.map((user: ExtendedUser) => {
                const additionalQuota = distribution[user.id] === undefined || distribution[user.id] === "" ? 0 : parseInt(distribution[user.id]) || 0;

                return {
                    userId: user.id,
                    pageQuota: user.pageQuota + additionalQuota,
                };
            });

            // Berechne das neue Firmenkontingent
            const newFreePageQuota = company.freePageQuota - totalDistribution;

            // Rufe Mutation auf
            await updateQuotaDistribution({
                variables: {
                    companyId: company.id,
                    userQuotas,
                    freePageQuota: newFreePageQuota,
                },
            });

            // Schließe Dialog und zeige Erfolgsmeldung
            setDistributeDialogOpen(false);
            dispatch(
                triggerNotification({
                    type: "success",
                    title: "Kontingent verteilt",
                    content: `${totalDistribution} Seiten wurden erfolgreich aus dem Firmenkontingent verteilt.`,
                    autoHideDuration: 5000,
                }),
            );
        } catch (error: any) {
            console.error("Fehler bei der Verteilung des Kontingents:", error);
            dispatch(
                triggerNotification({
                    type: "error",
                    title: "Fehler",
                    content: `Kontingent konnte nicht verteilt werden: ${error.message}`,
                    autoHideDuration: 5000,
                }),
            );
        }
    };

    // ==================== Enter Key Support for Dialogs ====================
    // User deletion dialog
    useEnterKeyDialog({
        isOpen: deleteDialogOpen,
        onConfirm: handleConfirmDelete,
    });

    // Invite link deletion dialog
    useEnterKeyDialog({
        isOpen: inviteDeleteDialogOpen,
        onConfirm: handleConfirmInviteDelete,
    });

    // Invite creation dialog
    useEnterKeyDialog({
        isOpen: openInviteDialog,
        onConfirm: handleCreateInviteLink,
        disabled: !inviteEmail || !!inviteErrors.email,
    });

    // Add seats dialog
    useEnterKeyDialog({
        isOpen: addSeatsDialogOpen,
        onConfirm: handleConfirmAddSeats,
        disabled: seatQuantities.S === 0 && seatQuantities.M === 0 && seatQuantities.L === 0,
    });

    // Quota distribution dialog
    useEnterKeyDialog({
        isOpen: distributeDialogOpen,
        onConfirm: handleConfirmDistribution,
    });

    // ==================== Rendering ====================
    return (
        <BaseSettingsPage title="Nutzerverwaltung" hasChanges={false} saveDisabled={true} useOuterScroll>
            <Grid2 container spacing={3}>
                {/* Verfügbare Seiten Card */}
                <SettingsCard title="Verfügbare Seiten" gridSpacing={0}>
                    <Grid2 container spacing={3} justifyContent="flex-start">
                        <Grid2 size={5}>
                            <Card
                                sx={{
                                    p: 2,
                                    textAlign: "center",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    borderRadius: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Firmenkontingent
                                    </Typography>
                                    <Typography variant="h4" gutterBottom>
                                        <Box component="span" sx={{ color: "text.primary", fontWeight: "bold" }}>
                                            {formatNumber(currentCompanyQuota)}
                                        </Box>{" "}
                                        / {formatNumber(originalTotal)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        verfügbare Seiten
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid2>
                        <Grid2 size={5} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", ml: 4 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate("/settings/shop/extra-pages")}
                                    sx={{ width: "100%", height: "50px" }}
                                >
                                    Firmenkontingent hinzufügen
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleOpenDistributeDialog}
                                    disabled={!company || company.freePageQuota <= 0}
                                    sx={{ width: "100%" }}
                                >
                                    Firmenkontingent verteilen
                                </Button>
                            </Box>
                        </Grid2>
                    </Grid2>

                    {!isValidTotal && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            Die Gesamtsumme der zugewiesenen Seiten darf {originalTotal} nicht überschreiten.
                        </Typography>
                    )}
                    {!areUserQuotasValid && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            Nutzerkontingente müssen mindestens so groß sein wie die bereits verbrauchten Seiten.
                        </Typography>
                    )}
                </SettingsCard>

                {/* Seats Overview Card */}
                <SettingsCard title="Sitze">
                    {/* Seat management overview */}
                    <Box sx={{ mb: 4 }}>
                        <Grid2 container spacing={2} justifyContent="center">
                            <Grid2 size={4}>
                                <Card
                                    sx={{
                                        p: 2,
                                        pt: 3,
                                        textAlign: "center",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        borderTop: `4px solid ${theme.palette.semantic.planSize.S}`,
                                        borderRadius: 2,
                                        position: "relative",
                                        bgcolor: 'common.white',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.S, fontWeight: "bold", fontSize: "1.2em", mr: "2px" }}>
                                                S
                                            </Box>
                                            -Sitze
                                        </Typography>
                                        <Typography variant="h4" gutterBottom>
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.S, fontWeight: "bold" }}>
                                                {formatNumber(seatOccupancy.S.occupied)}
                                            </Box>{" "}
                                            /{" "}
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.S }}>
                                                {formatNumber(seatOccupancy.S.total)}
                                            </Box>
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            belegt
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            bgcolor: theme.palette.semantic.planSize.S,
                                            color: "white",
                                            px: 1.5,
                                            py: 0.5,
                                            borderBottomLeftRadius: 8,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        S
                                    </Box>
                                </Card>
                            </Grid2>
                            <Grid2 size={4}>
                                <Card
                                    sx={{
                                        p: 2,
                                        pt: 3,
                                        textAlign: "center",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        borderTop: `4px solid ${theme.palette.semantic.planSize.M}`,
                                        borderRadius: 2,
                                        position: "relative",
                                        bgcolor: 'common.white',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.M, fontWeight: "bold", fontSize: "1.2em", mr: "2px" }}>
                                                M
                                            </Box>
                                            -Sitze
                                        </Typography>
                                        <Typography variant="h4" gutterBottom>
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.M, fontWeight: "bold" }}>
                                                {formatNumber(seatOccupancy.M.occupied)}
                                            </Box>{" "}
                                            /{" "}
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.M }}>
                                                {formatNumber(seatOccupancy.M.total)}
                                            </Box>
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            belegt
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            bgcolor: theme.palette.semantic.planSize.M,
                                            color: "white",
                                            px: 1.5,
                                            py: 0.5,
                                            borderBottomLeftRadius: 8,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        M
                                    </Box>
                                </Card>
                            </Grid2>
                            <Grid2 size={4}>
                                <Card
                                    sx={{
                                        p: 2,
                                        pt: 3,
                                        textAlign: "center",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        borderTop: `4px solid ${theme.palette.semantic.planSize.L}`,
                                        borderRadius: 2,
                                        position: "relative",
                                        bgcolor: 'common.white',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.L, fontWeight: "bold", fontSize: "1.2em", mr: "2px" }}>
                                                L
                                            </Box>
                                            -Sitze
                                        </Typography>
                                        <Typography variant="h4" gutterBottom>
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.L, fontWeight: "bold" }}>
                                                {formatNumber(seatOccupancy.L.occupied)}
                                            </Box>{" "}
                                            /{" "}
                                            <Box component="span" sx={{ color: theme.palette.semantic.planSize.L }}>
                                                {formatNumber(seatOccupancy.L.total)}
                                            </Box>
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            belegt
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            bgcolor: theme.palette.semantic.planSize.L,
                                            color: "white",
                                            px: 1.5,
                                            py: 0.5,
                                            borderBottomLeftRadius: 8,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        L
                                    </Box>
                                </Card>
                            </Grid2>
                        </Grid2>

                        {/* Zentraler "Sitze hinzufügen" Button */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(RouteToSettings.SHOP_SAAS)}
                                /* onClick={handleOpenAddSeatsDialog} */ size="large"
                            >
                                Sitze hinzufügen
                            </Button>

                            {/* Subtiler "Sitze entfernen" Button */}
                            <Button
                                variant="text"
                                onClick={() => navigate(RouteTo.CONTACT)}
                                size="small"
                                sx={{ mt: 1, opacity: 0.5, color: theme.palette.text.secondary }}
                            >
                                Sitze entfernen
                            </Button>
                        </Box>
                    </Box>

                    <Grid2 container spacing={2} sx={{ mb: 3 }}>
                        <Grid2 size={4} sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                            <Typography variant="h6" gutterBottom align="center">
                                S-Sitze
                            </Typography>
                            {seatArrays.S.map((seat, index) => (
                                <SeatManagementRow
                                    key={`seat-S-${index}`}
                                    index={index}
                                    size={UserSize.S}
                                    user={seat.user}
                                    inviteLink={seat.inviteLink}
                                    onCreateInvite={handleCreateInviteForSize}
                                    onDeleteSeat={handleRemoveSeat}
                                    onUserQuotaChange={(userId, value) => handleUserQuotaChange(userId, value)}
                                    onUserRoleChange={(userId, role) => handleRoleChange(userId, role)}
                                    onUserSizeChange={(userId, size) => handleSizeChange(userId, size)}
                                    onUserDelete={handleDeleteClick}
                                    onUserSwap={handleSwapClick}
                                    onInviteLinkDelete={handleInviteDeleteClick}
                                    onInviteLinkSwap={handleInviteLinkSwapClick}
                                    isQuotaInvalid={!!invalidUserQuotas[seat.user?.id ?? ""]}
                                    totalSeats={seatsS}
                                    isAdmin={!!seat.user && seat.user.id === company?.ownerId}
                                />
                            ))}
                        </Grid2>
                        <Grid2 size={4} sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                            <Typography variant="h6" gutterBottom align="center">
                                M-Sitze
                            </Typography>
                            {seatArrays.M.map((seat, index) => (
                                <SeatManagementRow
                                    key={`seat-M-${index}`}
                                    index={index}
                                    size={UserSize.M}
                                    user={seat.user}
                                    inviteLink={seat.inviteLink}
                                    onCreateInvite={handleCreateInviteForSize}
                                    onDeleteSeat={handleRemoveSeat}
                                    onUserQuotaChange={(userId, value) => handleUserQuotaChange(userId, value)}
                                    onUserRoleChange={(userId, role) => handleRoleChange(userId, role)}
                                    onUserSizeChange={(userId, size) => handleSizeChange(userId, size)}
                                    onUserDelete={handleDeleteClick}
                                    onUserSwap={handleSwapClick}
                                    onInviteLinkDelete={handleInviteDeleteClick}
                                    onInviteLinkSwap={handleInviteLinkSwapClick}
                                    isQuotaInvalid={!!invalidUserQuotas[seat.user?.id ?? ""]}
                                    totalSeats={seatsM}
                                    isAdmin={!!seat.user && seat.user.id === company?.ownerId}
                                />
                            ))}
                        </Grid2>
                        <Grid2 size={4} sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                            <Typography variant="h6" gutterBottom align="center">
                                L-Sitze
                            </Typography>
                            {seatArrays.L.map((seat, index) => (
                                <SeatManagementRow
                                    key={`seat-L-${index}`}
                                    index={index}
                                    size={UserSize.L}
                                    user={seat.user}
                                    inviteLink={seat.inviteLink}
                                    onCreateInvite={handleCreateInviteForSize}
                                    onDeleteSeat={handleRemoveSeat}
                                    onUserQuotaChange={(userId, value) => handleUserQuotaChange(userId, value)}
                                    onUserRoleChange={(userId, role) => handleRoleChange(userId, role)}
                                    onUserSizeChange={(userId, size) => handleSizeChange(userId, size)}
                                    onUserDelete={handleDeleteClick}
                                    onUserSwap={handleSwapClick}
                                    onInviteLinkDelete={handleInviteDeleteClick}
                                    onInviteLinkSwap={handleInviteLinkSwapClick}
                                    isQuotaInvalid={!!invalidUserQuotas[seat.user?.id ?? ""]}
                                    totalSeats={seatsL}
                                    isAdmin={!!seat.user && seat.user.id === company?.ownerId}
                                />
                            ))}
                        </Grid2>
                    </Grid2>
                </SettingsCard>

                <Dialog
                    open={isSwitchUserDialogOpen}
                    onClose={() => {
                        dispatch(setIsSwitchUserDialogOpen(false));
                        dispatch(setSelectedSwitchSeatType(null));
                    }}
                >
                    <DialogTitle>Seat tauschen</DialogTitle>

                    <DialogContent>
                        <Stack>
                            <Typography>
                                Wähle einen der folgenden verfügbaren Seats aus, um diesen mit Deinem <strong>{selectedSwitchSeatType || ""}</strong> Seat zu tauschen.
                            </Typography>
                        </Stack>
                    </DialogContent>

                    <DialogActions>
                        {getAvailableSeats().map((size) => (
                            <Button
                                key={size}
                                variant="contained"
                                onClick={() => handleSeatSwitch(size as UserSize)}
                                sx={{
                                    backgroundColor: theme.palette.semantic.planSize[size],
                                    "&:hover": {
                                        backgroundColor: theme.palette.semantic.planSize[size],
                                        opacity: 0.9,
                                    },
                                }}
                            >
                                {size}
                            </Button>
                        ))}
                        <Button
                            variant="outlined"
                            onClick={() => {
                                dispatch(setIsSwitchUserDialogOpen(false));
                                dispatch(setSelectedSwitchSeatType(null));
                            }}
                        >
                            Abbrechen
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Nutzer löschen Dialog */}
                <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                    <DialogTitle>Nutzer löschen</DialogTitle>
                    <DialogContent>{userToDelete && <Typography>Möchtest Du den Nutzer "{userToDelete.name}" wirklich löschen?</Typography>}</DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={handleCloseDeleteDialog}>
                            Abbrechen
                        </Button>
                        <Button variant="contained" onClick={handleConfirmDelete} color="error">
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Einladungslink erstellen Dialog */}
                <Dialog open={openInviteDialog} onClose={handleCloseInviteDialog}>
                    <DialogTitle>Einladungslink erstellen</DialogTitle>
                    <DialogContent>
                        {Object.keys(inviteErrors).length > 0 && inviteErrors.submit && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {inviteErrors.submit}
                            </Alert>
                        )}
                        <TextField
                            autoFocus
                            margin="dense"
                            label="E-Mail"
                            type="email"
                            fullWidth
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            error={!!inviteErrors.email}
                            helperText={inviteErrors.email}
                        />
                        <FormControl fullWidth margin="dense">
                            <InputLabel id="invite-role-label">Rolle</InputLabel>
                            <Select labelId="invite-role-label" value={inviteRole} label="Rolle" onChange={(e) => setInviteRole(e.target.value as UserRole)}>
                                <MenuItem value={UserRole.Admin}>ADMIN</MenuItem>
                                <MenuItem value={UserRole.Editor}>EDITOR</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Größenauswahl wurde entfernt, da sie automatisch basierend aus der Erstellung des Einladungslinks festgelegt wird */}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseInviteDialog}>Abbrechen</Button>
                        <Button onClick={handleCreateInviteLink}>Erstellen</Button>
                    </DialogActions>
                </Dialog>

                {/* Einladungslink löschen Dialog */}
                <Dialog open={inviteDeleteDialogOpen} onClose={handleCloseInviteDeleteDialog}>
                    <DialogTitle>Einladungslink löschen</DialogTitle>
                    <DialogContent>
                        {inviteLinkToDelete && <Typography>Möchtest Du den Einladungslink für "{inviteLinkToDelete.email}" wirklich löschen?</Typography>}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={handleCloseInviteDeleteDialog}>
                            Abbrechen
                        </Button>
                        <Button variant="contained" onClick={handleConfirmInviteDelete} color="error">
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog zum Hinzufügen von Sitzplätzen */}
                <Dialog open={addSeatsDialogOpen} onClose={handleCloseAddSeatsDialog}>
                    <DialogTitle>Sitze hinzufügen</DialogTitle>
                    <DialogContent sx={{ minWidth: 400 }}>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Wählen Sie die Anzahl der Sitze, die Sie hinzufügen möchten.
                        </Typography>

                        <Grid2 container spacing={2}>
                            <Grid2 size={4}>
                                <Box sx={{ p: 2, border: `1px solid ${theme.palette.semantic.planSize.S}`, borderRadius: 1, textAlign: "center" }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.semantic.planSize.S, fontWeight: "bold" }}>
                                        S-Sitze
                                    </Typography>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={seatQuantities.S}
                                        onChange={(e) => handleSeatQuantityChange(UserSize.S, parseInt(e.target.value) || 0)}
                                        inputProps={{ min: 0 }}
                                        fullWidth
                                    />
                                </Box>
                            </Grid2>
                            <Grid2 size={4}>
                                <Box sx={{ p: 2, border: `1px solid ${theme.palette.semantic.planSize.M}`, borderRadius: 1, textAlign: "center" }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.semantic.planSize.M, fontWeight: "bold" }}>
                                        M-Sitze
                                    </Typography>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={seatQuantities.M}
                                        onChange={(e) => handleSeatQuantityChange(UserSize.M, parseInt(e.target.value) || 0)}
                                        inputProps={{ min: 0 }}
                                        fullWidth
                                    />
                                </Box>
                            </Grid2>
                            <Grid2 size={4}>
                                <Box sx={{ p: 2, border: `1px solid ${"semantic.planSize.L"}`, borderRadius: 1, textAlign: "center" }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, color: "semantic.planSize.L", fontWeight: "bold" }}>
                                        L-Sitze
                                    </Typography>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={seatQuantities.L}
                                        onChange={(e) => handleSeatQuantityChange(UserSize.L, parseInt(e.target.value) || 0)}
                                        inputProps={{ min: 0 }}
                                        fullWidth
                                    />
                                </Box>
                            </Grid2>
                        </Grid2>

                        <Typography variant="body2" sx={{ mt: 3, color: "text.secondary" }}>
                            Hinweis: In Zukunft wird das Hinzufügen von Sitzen zum Checkout führen.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAddSeatsDialog}>Abbrechen</Button>
                        <Button onClick={handleConfirmAddSeats} variant="contained" disabled={seatQuantities.S === 0 && seatQuantities.M === 0 && seatQuantities.L === 0}>
                            Hinzufügen
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog zum Verteilen des Firmenkontingents */}
                <Dialog
                    open={distributeDialogOpen}
                    onClose={handleCloseDistributeDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            padding: 1,
                        },
                    }}
                >
                    <DialogTitle sx={{ paddingTop: 3, paddingBottom: 1, paddingLeft: 3, paddingRight: 3 }}>Firmenkontingent verteilen</DialogTitle>
                    <DialogContent sx={{ paddingLeft: 3, paddingRight: 3 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                Verteilen Sie Seiten aus dem Firmenkontingent auf die Nutzer.
                            </Typography>
                            <Typography variant="h6" color="primary.main" fontWeight="500">
                                Verfügbar: {company?.freePageQuota || 0} Seiten
                            </Typography>
                        </Box>

                        {/* Tabelle mit Nutzerinformationen */}
                        <Box
                            sx={{
                                mb: 4,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 1.5,
                                },
                                bgcolor: "rgba(220, 240, 220, 0.2)",
                                borderRadius: 2,
                            }}
                        >
                            {/* Tabellenkopf */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                                    gap: 2,
                                    mb: 1,
                                    paddingTop: 2,
                                    paddingBottom: 2,
                                    paddingLeft: 2,
                                    paddingRight: 2,
                                    color: "text.secondary",
                                    fontSize: "0.875rem",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Box>Nutzer</Box>
                                <Box sx={{ textAlign: "center" }}>Aktuelle Quote</Box>
                                <Box sx={{ textAlign: "center" }}>Genutzte Seiten</Box>
                                <Box sx={{ textAlign: "center" }}>Zusätzliche Seiten</Box>
                                <Box></Box>
                            </Box>

                            {/* Nutzerzeilen */}
                            <Box>
                                {users.map((user: ExtendedUser, index) => (
                                    <Box
                                        key={user.id}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                                            gap: 2,
                                            paddingTop: 2,
                                            paddingBottom: 2,
                                            paddingLeft: 2,
                                            paddingRight: 2,
                                            alignItems: "center",
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        {/* Nutzername und Größe */}
                                        <Box>
                                            <Typography variant="body2">
                                                {user.name}
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    ({user.size})
                                                </Typography>
                                            </Typography>
                                        </Box>

                                        {/* Aktuelle Seitenkontingent */}
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="body2">{user.pageQuota.toLocaleString()}</Typography>
                                        </Box>

                                        {/* Bereits verbrauchte Seiten */}
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="body2">{user.usedPages.toLocaleString()}</Typography>
                                        </Box>

                                        {/* Eingabefeld für zusätzliche Seiten */}
                                        <Box sx={{ textAlign: "center" }}>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={distribution[user.id] === undefined ? "0" : distribution[user.id]}
                                                onChange={(e) => handleSetDistribution(user.id, e.target.value)}
                                                sx={{ width: "100px" }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Box>

                                        {/* Schnellauswahl-Buttons */}
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleAdjustDistribution(user.id, 50)}
                                                disabled={remainingCompanyQuota < 50}
                                                sx={{
                                                    minWidth: 0,
                                                    borderRadius: 6,
                                                    paddingLeft: 1.5,
                                                    paddingRight: 1.5,
                                                }}
                                            >
                                                +50
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleAdjustDistribution(user.id, -50)}
                                                disabled={(distribution[user.id] === "" ? 0 : parseInt(distribution[user.id] || "0")) < 50}
                                                sx={{
                                                    minWidth: 0,
                                                    borderRadius: 6,
                                                    paddingLeft: 1.5,
                                                    paddingRight: 1.5,
                                                    color: theme.palette.error.light,
                                                    borderColor: "rgba(211, 47, 47, 0.3)",
                                                    "&:hover": {
                                                        borderColor: theme.palette.error.light,
                                                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                                                    },
                                                }}
                                            >
                                                -50
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Übersicht der Verteilung */}
                        <Box
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                                bgcolor: "rgba(220, 240, 220, 0.2)",
                                border: "1px solid",
                                borderColor: remainingCompanyQuota < 0 ? "error.main" : "divider",
                                mb: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 3,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Verbleibendes Firmenkontingent:
                                    </Typography>
                                    <Typography variant="h6" color={remainingCompanyQuota < 0 ? "error" : "inherit"}>
                                        {remainingCompanyQuota}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Summe der Zuweisungen:
                                    </Typography>
                                    <Typography variant="h6" color={remainingCompanyQuota < 0 ? "error" : "inherit"}>
                                        {totalDistribution} / {company?.freePageQuota || 0}
                                    </Typography>
                                </Box>
                            </Box>

                            {remainingCompanyQuota < 0 && (
                                <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                                    Die Summe der Zuweisungen übersteigt das verfügbare Kontingent.
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ paddingLeft: 3, paddingRight: 3, paddingBottom: 3, paddingTop: 1 }}>
                        <Button onClick={handleCloseDistributeDialog} variant="outlined" sx={{ borderRadius: 6, paddingLeft: 3, paddingRight: 3 }}>
                            Abbrechen
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmDistribution}
                            disabled={totalDistribution === 0 || remainingCompanyQuota < 0}
                            sx={{ borderRadius: 6, paddingLeft: 3, paddingRight: 3 }}
                        >
                            Verteilen
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Unified Seat-Swap Dialog */}
                {(sourceUserForSwap || sourceInviteLinkForSwap) && (
                    <SeatSwapDialog
                        open={swapDialogOpen}
                        onClose={handleSwapDialogClose}
                        sourceUser={sourceUserForSwap || undefined}
                        sourceInviteLink={sourceInviteLinkForSwap || undefined}
                        users={users as any}
                        inviteLinks={inviteLinks as any}
                        emptySeatSizes={emptySeatSizes}
                    />
                )}

                {/* Error Snackbar */}
                <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Grid2>
        </BaseSettingsPage>
    );
};
